import React, { useEffect, useState, useContext, useRef } from 'react';
import { graphql, navigate } from 'gatsby';
import PropTypes from 'prop-types';
import { getImage, StaticImage, GatsbyImage } from 'gatsby-plugin-image';
import htmr from 'htmr';
import cookie from 'react-cookies';
import { Helmet } from 'react-helmet';
import {
  Accordion, Card,
  AccordionContext, useAccordionToggle,
} from 'react-bootstrap';
import * as styles from '../styles/pages/coursepage.module.scss';
import UtmService from './UtmService';
import Footer from '../components/footer';

function CoursePage({ data }) {
  const [showDescription, setShowDescription] = useState(false);
  const trustpilotRef = useRef(null);
  const pageData = data.brandEducationCourse;
  const imageData = data.courseImageAsset;
  const overviewContent = pageData?.learningobjectives;
  const image = imageData && imageData.localFile
    && getImage(imageData.localFile.childImageSharp.gatsbyImageData);
  const moduleDescription = pageData?.modules?.[0]?.moduledescription;
  const courseDescription = pageData?.coursedescription;
  const partnerIdentifier = cookie.load('partner_identifier');
  const partnerLogo = `${process.env.AssetsCDN}/partners/${partnerIdentifier}/header-logo.png`;
  const moduleData = pageData.modules;

  const moduleNumber = moduleData.length;
  const moduleLessonsNumber = moduleNumber*8;
  const numberofWeeks = moduleLessonsNumber/2;

  useEffect(() => {
    new UtmService().init();
    cookie.save('coursePicked', pageData.courseslug, {
      path: '/',
      domain: process.env.CookieDomain,
    });
    initTrustpilot();
    setTimeout(() => {
      window.dataLayer.push({
        event: 'view-page-action',
        'view-page-param': {
          studentId: cookie.load('student_id') || '',
          pageKind: 'CoursePage',
          locale: 'en',
        },
      });
    }, 50);
  }, []);

  const metaTitle = pageData?.coursename;
  const metaDescriptionHtml = pageData?.coursedescription;
  const metaDescription = metaDescriptionHtml.replaceAll(/<[^>]*>?/gm, '');
  const courseslug = pageData?.courseslug;
  const ogImage = `${process.env.AssetsCDN}/course-images/en/${courseslug}/website-square.png`;
  const domain = process.env.WebsiteDomain;
  const ogUrl = `${domain}/en/course-page/${courseslug}`;

  const ContextAwareToggle = ({ children, eventKey, callback }) => {
    const currentEventKey = useContext(AccordionContext);
    const decoratedOnClick = useAccordionToggle(
      eventKey,
      () => callback && callback(eventKey),
    );

    const isCurrentEventKey = currentEventKey === eventKey;
    return (
      <Accordion.Toggle
        as={Card.Header}
        className={isCurrentEventKey
          ? 'moduleTitleSection active' : 'moduleTitleSection'}
        onClick={decoratedOnClick}
      >
        {children}
      </Accordion.Toggle>
    );
  };
  ContextAwareToggle.propTypes = {
    children: PropTypes.any,
    eventKey: PropTypes.string,
    callback: PropTypes.func,
  };

  const initTrustpilot = () => {
    if (trustpilotRef.current && window.Trustpilot) {
      window.Trustpilot.loadFromElement(trustpilotRef.current, true);
    }
  };

  const searchTerm = pageData.coursename.replace(/[^\w ]/g, '');

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <meta name="description" content={metaDescription} />
        <meta name="title" content={metaTitle} />
        <title>
          {' '}
          {metaTitle}
          {' '}
        </title>
        <meta property="og:title" content={metaTitle} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={ogUrl} />
        <script type="text/javascript" src="//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js" async></script>
      </Helmet>
      <header className={styles.coursePageHeader}>
        <StaticImage
          src="../images/logo.png"
          width={156}
          quality={95}
          alt="Upskillist"
        />
        {partnerIdentifier
          && (partnerIdentifier !== 'unknown') && (
            <>
              <div className={styles.rightBorder} />
              <img
                src={partnerLogo}
                className={styles.partnerImage}
                alt="Partner Logo"
              />
            </>
          )}
      </header>
      <main>
        <section className={styles.contentSection}>
          <div className={styles.topSection}>
            {image && (
              <GatsbyImage
                className={styles.image}
                image={image}
                alt={pageData.coursename}
              />
            )}
            <div className={styles.courseDescriptionContainer}>
              <div className={styles.courseTextContainer}>
                {/* <p className={styles.text}>Learn the basics of</p> */}
                <h1>{pageData.coursename}</h1>
                <div className={`${styles.courseDesc} ${showDescription
                  ? styles.showMoreContent : ''}
                  ${overviewContent ? styles.descriptionText : ''}`}
                >
                  {(courseDescription && htmr(courseDescription))
                    || (moduleDescription && htmr(moduleDescription))}
                </div>
                {!overviewContent ?
                <button
                  className={styles.readMoreCta}
                  type="button"
                  onClick={() => setShowDescription(!showDescription)}
                >
                  {showDescription ? 'Read Less' : 'Read More'}
                </button> : null
                }
              </div>
              <div className={styles.enrollCta}>
                <button onClick={() => navigate(`${process.env.SignupLink}?courseId=${pageData.courseslug}`)}>
                  Enroll now
                </button>
                <p>Sign up to get immediate access to all of your course content.</p>
              </div>
            </div>
          </div>
          <div className={styles.infoSection}>
            <div className={styles.infoContainer}>
              <div className={styles.leftSection}>
                <div ref={trustpilotRef} className="trustpilot-widget" data-locale="en-US" data-template-id="5419b6ffb0d04a076446a9af"
                  data-businessunit-id="6194a9df58af49f369c74f47" data-style-height="50px" data-style-width="200px">
                  <a href="https://www.trustpilot.com/review/upskillist.com" target="_blank" rel="noopener">Trustpilot</a>
                </div>
              </div>
              <div className={styles.rightSection}>
                <h2>Recognized by -</h2>
                {/* <div className={styles.apsu}>
                  <div className={styles.hideOnMobile}>
                    <StaticImage
                      placeholder="blurred"
                      src="../images/apsu.png"
                      width={150}
                      height={60}
                      alt="APSU"
                    />
                  </div>
                  <div className={styles.showOnMobile}>
                    <StaticImage
                      placeholder="blurred"
                      src="../images/apsu-mobile.png"
                      width={120}
                      height={48}
                      alt="APSU"
                    />
                  </div>
                </div> */}
                <div className={styles.apsu}>
                  <div className={styles.hideOnMobile}>
                    <StaticImage
                      placeholder="blurred"
                      src="../images/cpd.png"
                      width={66}
                      height={60}
                      alt="APSU"
                    />
                  </div>
                  <div className={styles.showOnMobile}>
                    <StaticImage
                      placeholder="blurred"
                      src="../images/cpd.png"
                      width={52}
                      height={48}
                      alt="APSU"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          {overviewContent && overviewContent.length !== 0 ?
          <div className={styles.learnContent}>
            <div className={styles.courseHeader}>
              <div className={styles.headerTitle}>
                <h2>Your course</h2>
                <h3>Overview</h3>
              </div>
            </div>
            <div className={styles.overviewFlexBox}>
              <div className={styles.leftOverviewSection}>
                <ul>
                  {overviewContent.map((content, index) => (
                    <li key={index}>{content}</li>
                  ))}
                 </ul>
              </div>
              <div className={styles.rightOverviewSection}>
                <div className={styles.row}>
                  <div className={styles.column}>
                    <div className={styles.triangle}></div>
                    <div className={styles.columnContent}>
                      <h3>{numberofWeeks} Weeks</h3>
                      <p>Duration</p>
                    </div>
                  </div>
                  <div className={styles.column}>
                    <div className={styles.triangle}></div>
                    <div className={styles.columnContent}>
                      <h3>{moduleLessonsNumber} Lessons</h3>
                      <p>Plus toolkits</p>
                    </div>
                  </div>
                </div>
                <div className={styles.row}>
                  <div className={styles.column}>
                    <div className={styles.triangle}></div>
                    <div className={styles.columnContent}>
                      <h3>{moduleNumber} Modules</h3>
                      <p>Online</p>
                    </div>
                  </div>
                  <div className={styles.column}>
                    <div className={styles.triangle}></div>
                    <div className={styles.columnContent}>
                      <h3>Diploma</h3>
                      <p>Certification</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div> : null}
          <div className={styles.courseDetailsSection}>
            <div className={styles.leftContentSection} id="content">
              <div className={styles.accordianContent}>
                <div className={styles.courseHeader}>
                  <div className={styles.headerTitle}>
                    <h2>Contents</h2>
                    <h3>Of your course.</h3>
                  </div>
                </div>
                <Accordion defaultActiveKey="module-0">
                  <div
                    className={styles.moduleContent}
                  >
                    {/*  */}
                    {pageData?.modules
                      .map((module, moduleIndex) => (
                        <Card key={moduleIndex} className={styles.accordionCard}>
                          <ContextAwareToggle
                            eventKey={`module-${moduleIndex}`}
                          >
                            <div className={styles.moduleTitle}>
                              <p>
                                module
                                {' '}
                                {moduleIndex + 1}
                              </p>
                            </div>
                            <div className={styles.moduleData}>
                              <h3>
                                {module.modulename}
                              </h3>
                              <div className={styles.accessSection}>
                                <p className={styles.addIcon}>+</p>
                                <p className={styles.addIconMinus}>
                                  -
                                </p>
                              </div>
                            </div>
                          </ContextAwareToggle>
                          <Accordion.Collapse
                            eventKey={`module-${moduleIndex}`}
                          >
                            <Card.Body>
                              <ol>
                                {module.lessons
                                  .map((lesson, lessonIndex) => (
                                    <li key={lessonIndex}>
                                      <details>
                                        <summary>
                                          {lesson.lessonname}
                                        </summary>
                                        {htmr(lesson.lessondescription)}
                                      </details>
                                    </li>
                                  ))}
                              </ol>
                            </Card.Body>
                          </Accordion.Collapse>
                        </Card>
                      ))}
                  </div>
                </Accordion>
              </div>
            </div>
          </div>
          <div className={styles.standOut}>
            <div className={styles.courseHeader}>
              <div className={styles.headerTitle}>
                <h2>What makes us</h2>
                <h3>Stand out</h3>
              </div>
            </div>
            <div className={styles.standOutContent}>
              <div className={styles.individualSection}>
                <div className={styles.topIntSection}>
                  <StaticImage
                    placeholder="blurred"
                    src="../images/Icon-experts.png"
                    width={41}
                    height={48}
                    alt="Experts"
                  />
                  <h3>In-house experts</h3>
                </div>
                <div className={styles.middleSection}>
                  <p>We don&apos;t outsource our content.
                    Every course we make is our own and must meet our standards.</p>
                </div>
                <div className={styles.bottomIntSection}>
                  <StaticImage
                    placeholder="blurred"
                    src="../images/Icon-plus.png"
                    width={12}
                    height={12}
                    alt="Experts"
                  />
                  <a href={`/about`} target="_blank">
                    <h3>Learn more about us.</h3></a>
                </div>
              </div>
              <div className={styles.individualSection}>
                <div className={styles.topIntSection}>
                  <StaticImage
                    placeholder="blurred"
                    src="../images/Icon-certificate.png"
                    width={46}
                    height={48}
                    alt="Experts"
                  />

                  <h3>Shareable certificate</h3>
                </div>
                <div className={styles.middleSection}>
                  <p>Earn your globally recognised certificate and add it to your LinkedIn profile.</p>
                </div>
                <div className={styles.bottomIntSection}>
                  <StaticImage
                    placeholder="blurred"
                    src="../images/Icon-plus.png"
                    width={13}
                    height={12}
                    alt="Experts"
                  />
                  <a href={`/validation/`} target="_blank">
                    <h3>Learn about our certificates.</h3></a>
                </div>
              </div>
              <div className={styles.individualSection}>
                <div className={styles.topIntSection}>
                  <StaticImage
                    placeholder="blurred"
                    src="../images/Icon-jobs.png"
                    width={64}
                    height={49}
                    alt="Experts"
                  />

                  <h3>Careers service</h3>
                </div>
                <div className={styles.middleSection}>
                  <p>Use what you&apos;ve learnt to advance your career with our in-house careers service.</p>
                </div>
                <div className={styles.bottomIntSection}>
                  <StaticImage
                    placeholder="blurred"
                    src="../images/Icon-arrow.png"
                    width={13}
                    height={12}
                    alt="Experts"
                  />
                  <a href={`https://jobbio.upskillist.com/jobs/search/jobs?searchTerm=${searchTerm}`}
                    target="__blank">
                    <h3>Check out relevant jobs.</h3></a>
                </div>
              </div>
            </div>
          </div>
        </section>
        {/* Subscriber Section */}
        <div className={styles.subscribeSection}>
          <div className={styles.subscribeOuterContainer}>
            <div className={styles.subscribeInnerContainer}>
              <div className={styles.textSection}>
                <h2>Actionable skills,</h2>
                <h3>Infinite possibilities.</h3>
                <p>Unique lessons curated by in-house experts!</p>
              </div>
                <div className={styles.benefitsOuterContainer}>
                  <div className={styles.benefitsInnerContainer}>
                    <div className={styles.benefits}>
                      <h3>What you&apos;ll get</h3>
                      <ul>
                        <li>Choose from <span>80+ courses</span> to learn.</li>
                        <li>Courses range in length up to <span>32 hours.</span></li>
                        <li>Schedule or <span>watch when you want.</span></li>
                        <li>Industry leading <span>customer support.</span></li>
                        <li><span>Course materials</span> and <span>
                          eCerts</span> available for purchase</li>
                      </ul>
                    </div>
                    <div className={styles.separator}></div>
                    <div className={styles.ctaContainer}>
                      <button onClick={() => navigate(`${process.env.SignupLink}?courseId=${pageData.courseslug}/`)}>
                        Enroll now
                      </button>
                      <p>Sign up now and get immediate access to all of your course content</p>
                    </div>
                  </div>
                </div>
            </div>
          </div>
        </div>
        <footer className={styles.coursePageFooter}>
          <p>Got a question? Contact our support staff, we will be happy to help&nbsp;
            <a href="mailto:support@upskillist.com">support@upskillist.com</a></p>
        </footer>
      </main>
      <Footer />
    </>
  );
}

CoursePage.defaultProps = {
  data: {},
};

CoursePage.propTypes = {
  data: PropTypes.shape({
    brandEducationCourse: PropTypes.shape({
      coursename: PropTypes.string,
      coursedescription: PropTypes.string,
      courseslug: PropTypes.string,
      modules: PropTypes.arrayOf(PropTypes.shape({
        modulename: PropTypes.string,
        moduledescription: PropTypes.string,
        lessons: PropTypes.arrayOf(PropTypes.shape({
          lessonname: PropTypes.string,
          lessondescription: PropTypes.string,
        })),
      })),
    }),
    courseImageAsset: PropTypes.shape(PropTypes.object),
  }),
};

export default CoursePage;

export const pageQuery = graphql`
  query CourseLandingPageID($id: String, $courseImageS3Key: String) {
    brandEducationCourse(id: { eq: $id }) {
      coursename
      coursedescription
      learningobjectives
      courseslug
      modules {
        modulename
        moduledescription
        lessons {
          lessonname
          lessondescription
        }
      }
    }
    courseImageAsset: s3Object(Key: {eq: $courseImageS3Key}) {
      Key
      localFile {
        childImageSharp {
          gatsbyImageData(placeholder: BLURRED)
        }
      }
    }
  }`;
